/**
 * @fileoverview Students / Classes roster ("Studio Quiet"). Segmented
 * Students|Classes control, search, student rows (with a quiet hover mic
 * shortcut), a class grid, and the Add-Student dialog.
 */

import { useState, useEffect, useRef, useMemo, useCallback, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "@/App";
import { accountFeatureEnabled } from "@/lib/orgFeatures";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button as UiButton } from "@/components/ui/button";
import { InstrumentInput } from "@/components/InstrumentInput";
import { ConsentBadge } from "@/components/ConsentBadge";
import { getClassKind } from "@/lib/classKinds";
import { groupStudentsIntoSections, ALPHABET } from "@/lib/studentSort";
import api, { getErrorMessage } from "@/lib/api";
import {
  AGE_BAND_OPTIONS,
  isMinorBand,
  consentAudienceHint,
  PARENT_EMAIL_REQUIRED_MESSAGE,
} from "@/lib/consent";
import { formatPhoneInput } from "@/lib/phone";
import { toast } from "sonner";
import { MARKETPLACE_ENABLED } from "@/lib/featureFlags";
import {
  Card, ListRow, Avatar, Eyebrow, Button, SegmentedControl, Icon, Tag,
} from "@/components/studio/primitives";

import { isImportedStub, isProgramRosterStudent, studentContactEmail } from "@/lib/importedStudents";

const StudentRoster = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext) || {};
  // An org can turn off `add_students`; the server 403 is the real gate, this
  // just avoids offering a dialog that can only end in a toast.
  const canAddStudents = accountFeatureEnabled(user, "add_students");
  // The org `classes` toggle gates CREATION server-side; existing classes stay
  // readable, editable and recordable. Hiding the tab is therefore cosmetic —
  // a class already created remains reachable at /classes/:id, and its group
  // lessons stay in the lesson feed.
  const canUseClasses = accountFeatureEnabled(user, "classes");
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState("students");
  // Archive (I2) is a STATE of a student, not a kind of one, so it is a quiet
  // view switch inside the Students tab rather than a third top-level segment
  // beside Classes — which would mix two different axes in one control.
  const [studentView, setStudentView] = useState("active");
  const [students, setStudents] = useState([]);
  // Program tags (Phase 2). A roster record carries no program — attribution
  // is schedule-only — so the server derives {student_id: [program_id]} from
  // attributed lessons and slots. A solo instructor gets an empty map and the
  // roster renders exactly as it did before this existed.
  const [programTags, setProgramTags] = useState({ students: {}, programs: [] });
  const [programFilter, setProgramFilter] = useState(null);
  // How a student is TAUGHT, which is a different axis from which program they
  // belong to: a bulk class import can put hundreds of names on a roster, and
  // without this the six private students an instructor sees every week are
  // buried among them. A student taught both ways appears under both, so only
  // "all" is guaranteed not to double-count.
  const [taughtVia, setTaughtVia] = useState("all");
  // Program tags default ON — an org instructor needs to know which roster
  // entries belong to a program — but stay switchable, because a teacher
  // scanning for one name does not need a chip on every row.
  const [showProgramTags, setShowProgramTags] = useState(true);
  const [classes, setClasses] = useState([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const emptyStudent = {
    first_name: "",
    last_name: "",
    email: "",
    instruments: "",
    address: "",
    parent_name: "",
    parent_email: "",
    parent_phone: "",
    age_band: "adult",
  };
  const [newStudent, setNewStudent] = useState(emptyStudent);
  const [parentEmailError, setParentEmailError] = useState("");
  // The server's `roster_limit_reached` detail, held so the dialog can say it
  // in place rather than relying on a toast the instructor may miss.
  const [rosterLimit, setRosterLimit] = useState(null);

  // The view is an explicit argument rather than a closure read, so the
  // callback has no dependencies and the effect below can depend on it.
  const fetchStudents = useCallback(async (signal, view) => {
    try {
      const response = await api.get(`/students?status=${view}`, signal ? { signal } : {});
      setStudents(response.data);
    } catch (_error) {
      if (!signal?.aborted) toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  }, []);

  // Program tags load once, independently of the roster's active/archived
  // view: the derivation is over lessons and slots, which the view doesn't
  // change. Best-effort — a failure here must never break the roster, so it
  // simply leaves the chips off.
  useEffect(() => {
    if (!MARKETPLACE_ENABLED) return;
    let cancelled = false;
    api
      .get("/marketplace/roster-programs")
      .then((res) => {
        // Normalize on the way IN, not at each read site: this is decoration
        // over the roster, so a payload missing either key (an older server,
        // an empty response) must leave the page rendering exactly as it does
        // without programs at all.
        if (!cancelled) {
          setProgramTags({
            students: res.data?.students || {},
            programs: res.data?.programs || [],
            via: res.data?.via || {},
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const refreshAbortRef = useRef(null);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    fetchStudents(controller.signal, studentView);
    return () => {
      controller.abort();
      refreshAbortRef.current?.abort();
    };
  }, [studentView, fetchStudents]);

  // /students?add=1 opens the Add-Student dialog (Dashboard "+ Add student"
  // pill). Consume the param even when the org disabled adding, so a stale
  // deep link doesn't sit in the URL re-firing on every render.
  useEffect(() => {
    if (searchParams.get("add") === "1") {
      if (canAddStudents) setIsAddModalOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, canAddStudents]);

  // The Classes tab is hidden when the org disabled it — fall back to Students
  // so a stale tab state (or a future deep link) can't strand the page on a tab
  // with no control to leave it.
  useEffect(() => {
    if (!canUseClasses && tab === "classes") setTab("students");
  }, [canUseClasses, tab]);

  useEffect(() => {
    if (tab !== "classes") return undefined;
    const controller = new AbortController();
    setClassesLoading(true);
    api
      .get("/classes", { signal: controller.signal })
      .then((res) => setClasses(res.data || []))
      .catch((_error) => {
        if (!controller.signal.aborted) toast.error("Failed to load classes");
      })
      .finally(() => {
        if (!controller.signal.aborted) setClassesLoading(false);
      });
    return () => controller.abort();
  }, [tab]);

  const handleAddStudent = async (e) => {
    e.preventDefault();
    if (isMinorBand(newStudent.age_band) && !newStudent.parent_email.trim()) {
      setParentEmailError(PARENT_EMAIL_REQUIRED_MESSAGE);
      toast.error(PARENT_EMAIL_REQUIRED_MESSAGE);
      return;
    }
    setParentEmailError("");
    setRosterLimit(null);
    try {
      const studentData = {
        first_name: newStudent.first_name,
        last_name: newStudent.last_name,
        email: newStudent.email,
        instruments: newStudent.instruments.split(",").map((i) => i.trim()).filter((i) => i),
        address: newStudent.address || null,
        parent_name: newStudent.parent_name || null,
        parent_email: newStudent.parent_email.trim() || null,
        // Same as iOS: the guardian phone is only asked for a minor and is
        // left out of the payload when blank. Gating on the band (not just
        // the value) means a number typed before switching back to Adult —
        // where the field is hidden — never travels invisibly.
        ...(isMinorBand(newStudent.age_band) && newStudent.parent_phone.trim()
          ? { parent_phone: newStudent.parent_phone.trim() }
          : {}),
        age_band: newStudent.age_band,
      };
      await api.post("/students", studentData);
      toast.success(
        newStudent.age_band
          ? "Student added. Recording is locked — open their profile to send a consent request."
          : "Student added."
      );
      setIsAddModalOpen(false);
      setNewStudent(emptyStudent);
      refreshAbortRef.current = new AbortController();
      fetchStudents(refreshAbortRef.current.signal, studentView);
    } catch (error) {
      // A full roster is answered IN the dialog rather than only by a toast.
      // The instructor has just filled in six fields, the message offers two
      // ways forward, and one of them (archive) is on the page behind this
      // one — none of which survives a notification that fades.
      const detail = error.response?.data?.detail;
      if (detail?.code === "roster_limit_reached") setRosterLimit(detail);
      toast.error(getErrorMessage(error, "Failed to add student"));
    }
  };

  const filteredStudents = students.filter((student) => {
    const fullName = `${student.first_name || ""} ${student.last_name || ""}`.toLowerCase();
    const q = searchQuery.toLowerCase();
    // Program-roster and imported students can arrive with email (or an
    // instrument) null, so every searched field is guarded.
    const matchesQuery =
      fullName.includes(q) ||
      (student.email || "").toLowerCase().includes(q) ||
      (studentContactEmail(student) || "").toLowerCase().includes(q) ||
      (student.instruments || []).some((i) => String(i || "").toLowerCase().includes(q));
    if (!matchesQuery) return false;
    if (taughtVia !== "all" && !(programTags.via?.[student.student_id] || []).includes(taughtVia)) return false;
    if (!programFilter) return true;
    return (programTags.students[student.student_id] || []).includes(programFilter);
  });

  // Contacts-style sections: sorted by last name, grouped under A–Z + #.
  const sections = useMemo(
    () => groupStudentsIntoSections(filteredStudents),
    [filteredStudents],
  );
  const activeLetters = useMemo(
    () => new Set(sections.map((s) => s.letter)),
    [sections],
  );

  // Jump to the tapped letter's section, or the nearest following one.
  const jumpToLetter = (letter) => {
    const target = ALPHABET.indexOf(letter);
    const match =
      sections.find((s) => ALPHABET.indexOf(s.letter) >= target) ||
      sections[sections.length - 1];
    if (match) {
      document
        .getElementById(`roster-section-${match.letter}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const inputCls = "bg-[var(--studio-base)] border-[var(--studio-divider-strong)] text-[var(--text-primary)] mt-1 h-12 text-base";

  return (
    <div className="mx-auto max-w-[1100px] px-6 pb-32 pt-11 md:px-12" data-testid="student-roster">
      {/* Header */}
      <div className="mb-7 flex items-end justify-between">
        <div>
          <Eyebrow>Your roster</Eyebrow>
          <h1 className="mt-1.5 font-serif text-[40px] font-normal leading-none tracking-[-0.02em] text-[var(--text-primary)]">
            {tab === "classes" ? "Classes" : studentView === "archived" ? "Archived" : "Students"}
          </h1>
        </div>
        {tab === "classes" ? (
          <Button icon="plus" onPress={() => navigate("/classes/new")} data-testid="create-class-button">
            New class
          </Button>
        ) : /* Adding from the archived view would create an active student who
              is invisible from where you added them. */
        !canAddStudents || studentView === "archived" ? null : (
          <Dialog open={isAddModalOpen} onOpenChange={(open) => { setIsAddModalOpen(open); if (!open) { setParentEmailError(""); setRosterLimit(null); } }}>
            <DialogTrigger asChild>
              <Button icon="plus" data-testid="add-student-button">Add student</Button>
            </DialogTrigger>
            {/* Capped to the viewport and scrolling inside: a minor's form
                (guardian email and phone) is taller than a laptop screen, and
                a fixed, centred dialog with no cap clips its own title and
                its Add Student button off the top and bottom. */}
            <DialogContent className="studio bg-[var(--studio-overlay)] border border-[var(--studio-divider-strong)] text-[var(--text-primary)] mx-4 max-h-[calc(100dvh-2rem)] max-w-[calc(100%-2rem)] overflow-y-auto md:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl font-normal">Add New Student</DialogTitle>
              </DialogHeader>
              {rosterLimit && (
                <div
                  className="mt-4 rounded-xl border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-faint)] p-4"
                  data-testid="roster-limit-notice"
                >
                  {/* The server's own sentence, not a second copy of it. A
                      wall that reads one way in a 403 and another in the UI is
                      two things to keep in step for no benefit. */}
                  <p className="text-sm text-[var(--text-secondary)]">{rosterLimit.message}</p>
                </div>
              )}
              <form onSubmit={handleAddStudent} noValidate className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="first_name" className="text-sm text-[var(--text-tertiary)]">First Name</Label>
                    <Input id="first_name" value={newStudent.first_name} onChange={(e) => setNewStudent({ ...newStudent, first_name: e.target.value })} className={inputCls} required data-testid="student-first-name-input" />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="text-sm text-[var(--text-tertiary)]">Last Name</Label>
                    <Input id="last_name" value={newStudent.last_name} onChange={(e) => setNewStudent({ ...newStudent, last_name: e.target.value })} className={inputCls} data-testid="student-last-name-input" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm text-[var(--text-tertiary)]">Email</Label>
                  <Input id="email" type="email" value={newStudent.email} onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })} className={inputCls} required data-testid="student-email-input" />
                </div>
                <div>
                  <Label htmlFor="age_band" className="text-sm text-[var(--text-tertiary)]">Age Group</Label>
                  <select
                    id="age_band"
                    value={newStudent.age_band}
                    onChange={(e) => { setNewStudent({ ...newStudent, age_band: e.target.value }); setParentEmailError(""); }}
                    className="mt-1 h-12 w-full rounded-md border border-[var(--studio-divider-strong)] bg-[var(--studio-base)] px-3 text-base text-[var(--text-primary)] focus:border-[var(--accent-yellow-border-strong)] focus:outline-none"
                    data-testid="student-age-band-select"
                  >
                    {AGE_BAND_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-[var(--text-quaternary)]">{consentAudienceHint(newStudent.age_band)}</p>
                </div>
                <div>
                  <Label htmlFor="parent_name" className="text-sm text-[var(--text-tertiary)]">Parent / Guardian Name</Label>
                  <Input id="parent_name" value={newStudent.parent_name} onChange={(e) => setNewStudent({ ...newStudent, parent_name: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <Label htmlFor="parent_email" className="text-sm text-[var(--text-tertiary)]">
                    {isMinorBand(newStudent.age_band) ? "Parent / Guardian Email *" : "Additional Email (optional)"}
                  </Label>
                  <Input
                    id="parent_email"
                    type="email"
                    value={newStudent.parent_email}
                    onChange={(e) => { setNewStudent({ ...newStudent, parent_email: e.target.value }); if (parentEmailError) setParentEmailError(""); }}
                    placeholder={isMinorBand(newStudent.age_band) ? "Required for minors" : "e.g. parent or guardian"}
                    required={isMinorBand(newStudent.age_band)}
                    aria-invalid={parentEmailError ? "true" : undefined}
                    className={`mt-1 h-12 bg-[var(--studio-base)] text-base text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)] ${parentEmailError ? "border-[var(--state-danger)]" : "border-[var(--studio-divider-strong)]"}`}
                    data-testid="student-parent-email-input"
                  />
                  {parentEmailError ? (
                    <p className="mt-1 text-xs text-[var(--state-danger)]" data-testid="parent-email-error">{parentEmailError}</p>
                  ) : isMinorBand(newStudent.age_band) ? (
                    <p className="mt-1 text-xs text-[var(--text-tertiary)]" data-testid="parent-email-hint">Required for students under 18</p>
                  ) : (
                    <p className="mt-1 text-xs text-[var(--text-quaternary)]">Lesson summaries will also be sent here</p>
                  )}
                </div>
                {/* Guardian phone — minors only, as on iOS. Optional: it is the
                    consent-SMS target once Twilio is configured, never a gate. */}
                {isMinorBand(newStudent.age_band) && (
                  <div>
                    <Label htmlFor="parent_phone" className="text-sm text-[var(--text-tertiary)]">Parent Phone</Label>
                    <Input
                      id="parent_phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      value={newStudent.parent_phone}
                      onChange={(e) => setNewStudent({ ...newStudent, parent_phone: formatPhoneInput(e.target.value) })}
                      placeholder="(555) 555-5555"
                      className={`${inputCls} placeholder:text-[var(--text-quaternary)]`}
                      data-testid="student-parent-phone-input"
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="instruments" className="text-sm text-[var(--text-tertiary)]">Instruments</Label>
                  <InstrumentInput id="instruments" value={newStudent.instruments} onChange={(e) => setNewStudent({ ...newStudent, instruments: e.target.value })} placeholder="Piano, Guitar, Violin" className="mt-1 h-12 bg-[var(--studio-base)] border-[var(--studio-divider-strong)] text-base text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)]" data-testid="student-instruments-input" />
                </div>
                <div>
                  <Label htmlFor="address" className="text-sm text-[var(--text-tertiary)]">Address</Label>
                  <Input id="address" value={newStudent.address} onChange={(e) => setNewStudent({ ...newStudent, address: e.target.value })} className={inputCls} data-testid="student-address-input" />
                </div>
                <div className="flex gap-3 pt-4">
                  <UiButton type="button" variant="outline" onClick={() => setIsAddModalOpen(false)} className="h-12 flex-1 border-[var(--studio-divider-strong)] text-[var(--text-primary)] hover:bg-[var(--studio-interactive)]">Cancel</UiButton>
                  <UiButton type="submit" className="h-12 flex-1 bg-[var(--accent-yellow)] text-[var(--text-inverse)]" data-testid="save-student-button">Add Student</UiButton>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Segmented control */}
      <SegmentedControl
        testId="roster-tabs"
        value={tab}
        onChange={setTab}
        className="mb-5"
        options={[
          // No count while the archived view is showing — the number would sit
          // under a "Students" label and read as the roster size.
          {
            id: "students",
            label: "Students",
            count: studentView === "active" ? students.length : undefined,
            testId: "tab-students",
          },
          ...(canUseClasses
            ? [{ id: "classes", label: "Classes", count: classes.length, testId: "tab-classes" }]
            : []),
        ]}
      />

      {tab === "classes" ? (
        classesLoading ? (
          <SkeletonList />
        ) : classes.length === 0 ? (
          <EmptyPanel testId="classes-empty" icon="users" title="No classes yet" sub="Group students for shared lessons" cta="Create class" onCta={() => navigate("/classes/new")} />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {classes.map((cls) => {
              const kind = getClassKind(cls.kind);
              const count = cls.member_ids?.length || 0;
              return (
                <Card key={cls.class_id} hover padding={20} onPress={() => navigate(`/classes/${cls.class_id}`)} data-testid={`class-card-${cls.class_id}`}>
                  <div className="font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">{kind.label}</div>
                  <div className="mt-1 font-sans text-lg font-semibold text-[var(--text-primary)]">{cls.name}</div>
                  <div className="mt-1 font-sans text-[13px] text-[var(--text-tertiary)]">{count} {count === 1 ? "student" : "students"} grouped</div>
                </Card>
              );
            })}
            <button
              type="button"
              onClick={() => navigate("/classes/new")}
              className="flex min-h-[120px] items-center justify-center gap-2 rounded-[20px] border border-dashed border-[var(--studio-divider-strong)] font-sans text-[15px] text-[var(--text-tertiary)] transition-colors hover:bg-[var(--studio-raised)]"
            >
              <Icon name="plus" size={16} /> New class
            </button>
          </div>
        )
      ) : (
        <>
          {/* Search */}
          <div className="mb-5 flex items-center gap-2.5 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-4">
            <Icon name="search" size={17} className="text-[var(--text-tertiary)]" />
            <Input
              placeholder="Search students…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-12 flex-1 border-none bg-transparent px-0 text-base text-[var(--text-primary)] shadow-none focus-visible:ring-0 placeholder:text-[var(--text-quaternary)]"
              data-testid="search-students-input"
            />
            {/* Beside the archived switch, not in the program filter bar: both
                are per-scan view preferences, and the filter bar is hidden for
                an instructor with a single program — who still has tags. */}
            {programTags.programs.length > 0 && (
              <button
                type="button"
                onClick={() => setShowProgramTags((on) => !on)}
                data-testid="toggle-program-tags"
                aria-pressed={showProgramTags}
                className="shrink-0 rounded-full border border-[var(--studio-divider)] px-3 py-1.5 font-sans text-[13px] text-[var(--text-tertiary)] transition-colors hover:bg-[var(--studio-interactive)]"
              >
                {showProgramTags ? "Tags on" : "Tags off"}
              </button>
            )}
            <button
              type="button"
              onClick={() => setStudentView(studentView === "active" ? "archived" : "active")}
              data-testid="toggle-archived-view"
              className="shrink-0 rounded-full border border-[var(--studio-divider)] px-3 py-1.5 font-sans text-[13px] text-[var(--text-tertiary)] transition-colors hover:bg-[var(--studio-interactive)]"
            >
              {studentView === "active" ? "Archived" : "Active"}
            </button>
          </div>

          {/* How each student is taught. Inside the Students tab rather than
              beside Classes: those are class RECORDS, these are the people in
              them, and one control mixing the two axes reads as four unrelated
              destinations. */}
          <SegmentedControl
            testId="taught-via-tabs"
            value={taughtVia}
            onChange={setTaughtVia}
            className="mb-5"
            options={[
              { id: "all", label: "All students", count: students.length, testId: "tab-all-students" },
              { id: "lesson", label: "Lessons", testId: "tab-lesson-students" },
              { id: "class", label: "Class students", testId: "tab-class-students" },
            ]}
          />

          {/* Program filter. Rendered only for an instructor who actually
              teaches across programs — for everyone else there is nothing to
              filter by and a lone "All" chip would be noise. */}
          {programTags.programs.length > 1 && (
            <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="program-filter">
              <button
                type="button"
                onClick={() => setProgramFilter(null)}
                data-testid="program-filter-all"
                className={[
                  "rounded-full border px-3 py-1.5 font-sans text-[13px] transition-colors",
                  programFilter === null
                    ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] text-[var(--text-primary)]"
                    : "border-[var(--studio-divider)] text-[var(--text-tertiary)] hover:bg-[var(--studio-interactive)]",
                ].join(" ")}
              >
                All programs
              </button>
              {programTags.programs.map((prog) => (
                <button
                  key={prog.program_id}
                  type="button"
                  onClick={() => setProgramFilter(programFilter === prog.program_id ? null : prog.program_id)}
                  data-testid={`program-filter-${prog.program_id}`}
                  className={[
                    "rounded-full border px-3 py-1.5 font-sans text-[13px] transition-colors",
                    programFilter === prog.program_id
                      ? "border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] text-[var(--text-primary)]"
                      : "border-[var(--studio-divider)] text-[var(--text-tertiary)] hover:bg-[var(--studio-interactive)]",
                  ].join(" ")}
                >
                  {prog.title}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <SkeletonList />
          ) : filteredStudents.length === 0 ? (
            studentView === "archived" ? (
              <EmptyPanel
                icon="archive"
                testId="archived-empty"
                title={searchQuery ? "No archived students found" : "Nothing archived"}
                sub={
                  searchQuery
                    ? "Try a different search"
                    : "Archiving keeps a student's lessons but takes them off your roster."
                }
              />
            ) : (
            <EmptyPanel
              icon="users"
              title={searchQuery ? "No students found" : "No students yet"}
              sub={
                searchQuery
                  ? "Try a different search"
                  : canAddStudents
                    ? "Add your first student"
                    : "Your organization manages this roster"
              }
              cta={searchQuery || !canAddStudents ? null : "Add student"}
              onCta={() => setIsAddModalOpen(true)}
            />
            )
          ) : (
            <div className={`relative ${sections.length > 1 ? "pr-5 md:pr-0" : ""}`}>
              <Card padding={6}>
                {sections.map((section) => (
                  <div
                    key={section.letter}
                    id={`roster-section-${section.letter}`}
                    className="scroll-mt-24"
                  >
                    <div
                      className="px-4 pb-1 pt-3 font-mono text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-tertiary)]"
                      data-testid={`roster-section-header-${section.letter}`}
                    >
                      {section.title}
                    </div>
                    {section.data.map((student, i) => {
                      const name = `${student.first_name || ""} ${student.last_name || ""}`.trim();
                      const stub = isImportedStub(student);
                      return (
                        <ListRow
                          key={student.student_id}
                          data-testid={`student-card-${student.student_id}`}
                          leading={<Avatar name={name} size={48} accent />}
                          title={name}
                          subtitle={
                            <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                              {/* A program roster's student has no contact
                                  details here, so no "No email yet" nudge. */}
                              {!isProgramRosterStudent(student) && (
                                <span className="inline-flex items-center gap-1.5">
                                  <Icon name="mail" size={12} className="text-[var(--text-tertiary)]" />
                                  {studentContactEmail(student) || "No email yet"}
                                </span>
                              )}
                              {/* Program chips: which program this student's
                                  lessons are attributed to. Derived, so a
                                  student can carry more than one. */}
                              {showProgramTags && (programTags.students[student.student_id] || []).map((pid) => {
                                const prog = programTags.programs.find((x) => x.program_id === pid);
                                return prog ? (
                                  <Tag key={pid} data-testid={`student-program-tag-${student.student_id}-${pid}`}>
                                    {prog.title}
                                  </Tag>
                                ) : null;
                              })}
                            </span>
                          }
                          trailing={
                            <div className="flex items-center gap-3">
                              {stub ? (
                                <span
                                  data-testid={`acuity-import-tag-${student.student_id}`}
                                  className="hidden items-center gap-[5px] rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] px-[11px] py-[5px] font-sans text-[13px] font-medium text-[var(--accent-yellow-text)] sm:inline-flex"
                                >
                                  Imported · needs review
                                </span>
                              ) : (
                                <ConsentBadge
                                  status={student.consent_status}
                                  programRoster={isProgramRosterStudent(student)}
                                  className="hidden sm:inline-flex"
                                />
                              )}
                              <Icon name="chevronRight" size={18} className="text-[var(--text-tertiary)]" />
                            </div>
                          }
                          borderBottom={i < section.data.length - 1}
                          onPress={() => navigate(`/students/${student.student_id}`)}
                          // No mic shortcut on an archived row: the server
                          // refuses the upload (student_archived), so the
                          // affordance could only ever produce a toast. Their
                          // page still opens, which is where Restore lives.
                          onRecord={student.archived ? undefined : () => navigate(`/hans/${student.student_id}`)}
                          recordLabel={`Record with ${name}`}
                        />
                      );
                    })}
                  </div>
                ))}
              </Card>

              {sections.length > 1 && (
                <nav
                  aria-label="Jump to letter"
                  data-testid="roster-jump-rail"
                  className="fixed right-1 top-1/2 z-20 flex -translate-y-1/2 flex-col items-center gap-px md:right-3"
                >
                  {ALPHABET.map((letter) => {
                    const active = activeLetters.has(letter);
                    return (
                      <button
                        key={letter}
                        type="button"
                        disabled={!active}
                        onClick={() => jumpToLetter(letter)}
                        aria-label={`Jump to ${letter}`}
                        className={`font-mono text-[10px] leading-[13px] transition-colors ${
                          active
                            ? "text-[var(--accent-yellow-text)] hover:opacity-70"
                            : "cursor-default text-[var(--text-quaternary)]"
                        }`}
                      >
                        {letter}
                      </button>
                    );
                  })}
                </nav>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const SkeletonList = () => (
  <div className="space-y-2">
    {[1, 2, 3, 4].map((i) => (
      <div key={i} className="skeleton h-16 rounded-xl" />
    ))}
  </div>
);

const EmptyPanel = ({ testId, icon, title, sub, cta, onCta }) => (
  <div className="rounded-[20px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-14 text-center" data-testid={testId}>
    <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--studio-interactive)]">
      <Icon name={icon} size={26} className="text-[var(--text-tertiary)]" />
    </div>
    <h3 className="mb-2 font-serif text-xl font-normal text-[var(--text-primary)]">{title}</h3>
    <p className="mb-6 font-sans text-sm text-[var(--text-tertiary)]">{sub}</p>
    {cta && <Button icon="plus" onPress={onCta}>{cta}</Button>}
  </div>
);

export default StudentRoster;
